/**
 * Centralized Amplitude event tracker using event delegation.
 *
 * Usage (Liquid):
 *   <a data-amp-event="Index Banner Clicked"
 *      data-amp-section="hero_carousel"
 *      data-amp-title="Banner Title"
 *      data-amp-position="1"
 *      href="/collections/sale">
 *
 * OSM: data-amp-osm-* (dataset keys like ampOsmCampaign) → props osm_campaign, osm_campaign_group, … (snake_case).
 * Other data-amp-* (except data-amp-event): slice(3) + lowercase first letter.
 *
 * Host gating for Amplitude is temporarily disabled (isAmplitudeHostAllowed always true) for analytics QA in dev.
 */

/** PascalCase suffix (e.g. CampaignGroup) → campaign_group */
function pascalRestToSnake(rest) {
  return rest
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_/, '');
}

/**
 * @param {string} key - dataset camelCase key, e.g. ampOsmCampaignGroup
 * @returns {string|null}
 */
export function ampOsmDatasetKeyToPropKey(key) {
  if (!key.startsWith('ampOsm') || key === 'ampEvent' || key.length <= 6) return null;
  const rest = key.slice(6);
  if (!rest) return null;
  return `osm_${pascalRestToSnake(rest)}`;
}

/**
 * @param {{ campaignId: string|number, groupId: string|number, contentType: string, templateType: string, triggerEvent: string }} p
 * @returns {Record<string, string>}
 */
export function buildOsmAmplitudeProps({ campaignId, groupId, contentType, templateType, triggerEvent }) {
  return {
    osm_campaign: String(campaignId),
    osm_campaign_group: String(groupId),
    osm_content_type: String(contentType),
    osm_template_type: String(templateType),
    osm_trigger_event: String(triggerEvent),
  };
}

/**
 * Temporarily always true so OSM / delegated events fire in dev for Amplitude QA.
 * Restore prod-only check: hostname wooltariusa.com | www.wooltariusa.com when done testing.
 */
export function isAmplitudeHostAllowed() {
  return true;
}

export function initAmplitudeTracker() {
  document.addEventListener('click', (e) => {
    if (!isAmplitudeHostAllowed()) return;

    const el = e.target.closest('[data-amp-event]');
    if (!el) return;

    const event = el.dataset.ampEvent;
    const props = {};

    for (const [key, value] of Object.entries(el.dataset)) {
      if (!key.startsWith('amp') || key === 'ampEvent') continue;

      const osmKey = ampOsmDatasetKeyToPropKey(key);
      if (osmKey) {
        props[osmKey] = value;
        continue;
      }

      const propName = key.slice(3).replace(/^./, (c) => c.toLowerCase());
      props[propName] = value;
    }

    window.amplitude?.track(event, props);
  });
}

export function setAmplitudeUser(customer = null) {
  setTimeout(() => {
    try {
      if (window.amplitude) {
        const identify = new window.amplitude.Identify();

        window.__isExpo = /Wooltari\/Expo/.test(navigator.userAgent);
        if (window.__isExpo) {
          identify.set('Access Platform', 'Mobile App');
          console.log('set amplitude platform');
        }

        if (customer) {
          identify.set('shopify_customer_id', customer.customerId);
          identify.set('customer_birth_date', customer.customerBirthDate);
          identify.set('customer_birth_date_basic', customer.customerBirthDateBasic);
        }

        window.amplitude.identify(identify);
        console.log('set amplitude identify');
      }
    } catch (e) {
      console.error(e);
    }
  }, 1000);
}